import {Component, effect, inject, OnInit, signal, untracked} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ErpOutboundPanelFilterService} from '../../service/erp-outbound-panel-filter.service';
import {ErpOutboundReportGroupedService} from '../../service/erp-outbound-report-grouped.service';
import {IErpOutboundItem, IErpOutboundOrderGroup} from '../../interfaces/erp-outbound-panel.interface';
import {erpOutboundResultChipStyle, isErpOutboundError} from '../../constants/erp-outbound-panel.constants';
import {ErpOutboundRawMessageDialogComponent} from '../erp-outbound-raw-message-dialog/erp-outbound-raw-message-dialog.component';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-erp-outbound-report-grouped-list',
  standalone: false,
  templateUrl: './erp-outbound-report-grouped-list.component.html',
  styleUrl: './erp-outbound-report-grouped-list.component.scss',
})
export class ErpOutboundReportGroupedListComponent implements OnInit {
  private readonly filterService = inject(ErpOutboundPanelFilterService);
  private readonly groupedService = inject(ErpOutboundReportGroupedService);
  private readonly dialog = inject(MatDialog);

  protected readonly masterColumns = [
    'expand', 'erpOrderNumber', 'legalEntityName', 'pnr', 'actionCount', 'latestResult', 'latestDateCreated',
  ];

  protected readonly groups = signal<IErpOutboundOrderGroup[]>([]);
  protected readonly loading = signal(false);
  protected readonly pageIndex = signal(0);
  protected readonly pageSize = signal(25);
  protected readonly totalItemCount = signal(0);

  private readonly expanded = new Set<string>();

  protected readonly chipStyle = erpOutboundResultChipStyle;

  constructor() {
    // Re-fetch (from the first page) whenever the panel applies new filters.
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      untracked(() => {
        this.pageIndex.set(0);
        this.load();
      });
    });
  }

  ngOnInit(): void {
    this.load();
  }

  protected refresh(): void {
    this.load();
  }

  protected isExpanded(group: IErpOutboundOrderGroup): boolean {
    return this.expanded.has(this.groupKey(group));
  }

  protected toggle(group: IErpOutboundOrderGroup): void {
    const key = this.groupKey(group);
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  protected onPage(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.load();
  }

  protected orderLabel(group: IErpOutboundOrderGroup): string {
    return group.erpOrderNumber?.trim() ? group.erpOrderNumber : '(no sales order)';
  }

  protected isError(action: IErpOutboundItem): boolean {
    return isErpOutboundError(action.result, action.errorDescription);
  }

  protected openRawMessage(action: IErpOutboundItem): void {
    if (!action.rawMessage) return;
    this.dialog.open(ErpOutboundRawMessageDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {title: 'Raw Message', content: action.rawMessage},
    });
  }

  protected openError(action: IErpOutboundItem): void {
    this.dialog.open(ErpOutboundRawMessageDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      data: {title: 'Error Description', content: action.errorDescription || 'No error description provided.'},
    });
  }

  private groupKey(group: IErpOutboundOrderGroup): string {
    return group.pnr ?? '';
  }

  private load(): void {
    this.loading.set(true);
    this.groupedService.getGroups({pageIndex: this.pageIndex(), pageSize: this.pageSize()}).subscribe({
      next: (response) => {
        this.groups.set(response.items ?? []);
        this.pageIndex.set((response.pagination?.pageNumber ?? 1) - 1);
        this.totalItemCount.set(response.pagination?.totalItemCount ?? 0);
        this.loading.set(false);
      },
      error: () => {
        this.groups.set([]);
        this.totalItemCount.set(0);
        this.loading.set(false);
      },
    });
  }
}
